import React, { useRef, useState } from "react";
import WarningIcon from "../../../../svg/warningIcon";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";

/** Shared muted red with the auth-letter mismatch badge and tooltip accent. */
const INVALID_RED = "#B42318";

/**
 * Renders a red count badge for required auth-letter fields that hold invalid
 * values (blank, "—", "NA", "0", non-numeric visit counts, ...). On hover,
 * shows a portal tooltip listing each affected field. Shown only on the
 * dark-green (reviewed, not-yet-validated) icon, where the user cannot reach
 * the reviewed-and-saved (light-green) state while the fields stay invalid.
 *
 * @input fieldNames - Human labels of the required fields holding invalid values
 * @return JSX.Element | null: Count badge with tooltip, or null when empty
 */
const InvalidFieldsWarning: React.FC<{ fieldNames?: string[] }> = ({
  fieldNames,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const fields = fieldNames ?? [];

  if (fields.length === 0) {
    return null;
  }

  return (
    <div
      ref={anchorRef}
      className="absolute -right-1.5 -top-1.5 z-10 flex h-[14px] min-w-[14px] shrink-0 cursor-default items-center justify-center rounded-full px-[3px] text-[9px] font-semibold leading-none text-white ring-1 ring-white"
      style={{ backgroundColor: INVALID_RED }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      aria-hidden="true"
    >
      {fields.length}
      <ToolTipUsingPortal
        placement="left"
        showTooltip={showTooltip}
        parentRef={anchorRef}
        maxWidth="300px"
      >
        <div className="flex flex-col gap-2 p-0.5">
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <WarningIcon
              height="13"
              width="13"
              outerFillColor={INVALID_RED}
              innerFillColor="#FFFFFF"
            />
            <span className="font-semibold text-primaryGray-1">
              Invalid values for:
            </span>
          </div>
          <div className="flex flex-col gap-1.5 border-t border-primaryGray-15 pt-1.5">
            {fields.map((label) => (
              <span
                key={label}
                className="whitespace-nowrap text-primaryGray-4"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </ToolTipUsingPortal>
    </div>
  );
};

export default InvalidFieldsWarning;
